import { useEffect, useState } from 'react';
import type { HITLRendererProps } from '../HITLRenderer';
import { hitlStyles, cn } from './styles';

export const CRMPreviewRenderer: React.FC<HITLRendererProps> = ({
  response,
  onSubmit,
  onCancel,
  isSubmitting,
  compact,
}) => {
  const { contact, plan, url } = response;
  const [editing, setEditing] = useState(false);
  const [editedChangesJson, setEditedChangesJson] = useState('[]');
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    const next = Array.isArray(plan?.changes) ? plan.changes : [];
    setEditedChangesJson(JSON.stringify(next, null, 2));
    setEditError(null);
  }, [plan]);

  const handleConfirm = () => {
    onSubmit('confirm');
  };

  const handleConfirmEdited = () => {
    try {
      const parsed = JSON.parse(editedChangesJson);
      if (!Array.isArray(parsed)) {
        setEditError('Le payload doit être un tableau JSON de changements.');
        return;
      }

      setEditError(null);
      onSubmit({
        decision: 'confirm',
        edited_payload: {
          confirmedChanges: parsed,
        },
      });
    } catch {
      setEditError('JSON invalide. Vérifiez la syntaxe.');
    }
  };

  const getModeLabel = (mode?: string) => {
    switch (mode) {
      case 'create': return 'Create New Contact';
      case 'update': return 'Update Existing Contact';
      case 'noop': return 'No Changes Needed';
      default: return 'CRM Operation';
    }
  };

  const getModeColor = (mode?: string) => {
    switch (mode) {
      case 'create': return 'text-green-600 bg-green-50';
      case 'update': return 'text-domino-aubergine bg-domino-lilac';
      case 'noop': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className={compact ? hitlStyles.containerCompact : hitlStyles.container}>
      {response.message && (
        <p className={hitlStyles.message}>{response.message}</p>
      )}

      {/* Contact Info Card */}
      {contact && (
        <div className={cn(hitlStyles.previewCard, 'mb-3')}>
          <h4 className={hitlStyles.previewTitle}>Contact</h4>
          <div className="space-y-1">
            {contact.fullName && (
              <p className="text-sm font-semibold text-gray-900">{contact.fullName}</p>
            )}
            {contact.title && (
              <p className="text-sm text-gray-600">{contact.title}</p>
            )}
            {contact.company && (
              <p className="text-sm text-gray-500">{contact.company}</p>
            )}
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
              {(contact.email || contact.emailGuess) && (
                <span className="flex items-center gap-1">
                  📧 {contact.email || contact.emailGuess}
                </span>
              )}
              {contact.location && (
                <span className="flex items-center gap-1">
                  📍 {contact.location}
                </span>
              )}
              {contact.linkedinUrl && (
                <a 
                  href={contact.linkedinUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-domino-prune hover:underline"
                >
                  🔗 LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plan Card */}
      {plan && (
        <div className={cn(hitlStyles.previewCard, 'mb-3')}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={hitlStyles.previewTitle}>CRM Action</h4>
            <span className={cn('px-2 py-0.5 rounded text-xs font-medium', getModeColor(plan.mode))}>
              {getModeLabel(plan.mode)}
            </span>
          </div>

          {plan.changes && plan.changes.length > 0 ? (
            <ul className="space-y-1.5">
              {plan.changes.slice(0, 8).map((change, idx) => (
                <li key={`${change.field}-${idx}`} className="text-sm">
                  <span className="font-medium text-gray-700">{change.field}:</span>{' '}
                  {change.from !== null && change.from !== undefined && (
                    <>
                      <span className={hitlStyles.previewChangeFrom}>{String(change.from)}</span>
                      {' → '}
                    </>
                  )}
                  <span className={hitlStyles.previewChangeTo}>
                    {String(change.to ?? '—')}
                  </span>
                </li>
              ))}
              {plan.changes.length > 8 && (
                <li className="text-xs text-gray-500 italic">
                  ...and {plan.changes.length - 8} more changes
                </li>
              )}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">No specific changes listed</p>
          )}
        </div>
      )}

      {/* URL Context */}
      {url && (
        <p className="text-xs text-gray-500 mb-3 truncate">
          Source: {url}
        </p>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-domino-cardBorder">
        {plan?.mode !== 'noop' && (
          <button
            onClick={() => setEditing((previous) => !previous)}
            disabled={isSubmitting}
            className={cn(hitlStyles.actionButton, hitlStyles.actionButtonSecondary)}
          >
            {editing ? 'Close editor' : 'Edit changes'}
          </button>
        )}
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className={cn(hitlStyles.actionButton, hitlStyles.actionButtonSecondary)}
          >
            Cancel
          </button>
        )}
        {plan?.mode !== 'noop' && (
          <button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className={cn(hitlStyles.actionButton, hitlStyles.actionButtonPrimary)}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className={hitlStyles.spinner} />
                Applying...
              </span>
            ) : (
              'Apply to HubSpot'
            )}
          </button>
        )}
      </div>

      {editing && plan?.mode !== 'noop' && (
        <div className="mt-3 rounded-lg border border-domino-cardBorder bg-domino-white p-3">
          <p className="mb-2 text-xs font-medium text-gray-700">Edit confirmedChanges JSON</p>
          <textarea
            value={editedChangesJson}
            onChange={(event) => setEditedChangesJson(event.target.value)}
            className="h-36 w-full rounded-md border border-gray-200 p-2 text-xs font-mono text-gray-700"
            spellCheck={false}
          />
          {editError && (
            <p className="mt-2 text-xs text-red-700">{editError}</p>
          )}
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleConfirmEdited}
              disabled={isSubmitting}
              className={cn(hitlStyles.actionButton, hitlStyles.actionButtonPrimary)}
            >
              Confirm with edits
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
